import { createContext, useContext, ReactNode } from 'react';
import { CmsComponentsMap } from './contracts';

/**
 * Carries the active component registry to nested `GeneratePage` renders (SC-1213).
 *
 * Why this exists alongside the explicit `componentsMap` prop, rather than replacing it:
 *
 * `GeneratePage -> Row -> Column -> Row` is a chain `GeneratePage` owns, so it threads the
 * map down as an ordinary prop — explicit, and type-checked at the two supply points
 * (`App.tsx` and `ServerApp.tsx`).
 *
 * `Tabs` is different. It is itself a CMS component, rendered *through* the map by
 * `Row`, and it renders a nested `<GeneratePage>` for the active tab's layout. Row cannot
 * hand it the map as a prop without spreading `componentsMap` into all 37 components'
 * props. So the one recursive consumer reads it from context instead.
 *
 * Without this, a lazy component nested inside a Tab would still render a placeholder —
 * the exact case the acceptance example (LeagueTable / VideoCarousel / PhotoAlbums in
 * tabs) is built from.
 */
const CmsComponentsMapContext = createContext<CmsComponentsMap | null>(null);

export const CmsComponentsMapProvider = ({
  componentsMap,
  children,
}: {
  componentsMap: CmsComponentsMap;
  children: ReactNode;
}) => <CmsComponentsMapContext.Provider value={componentsMap}>{children}</CmsComponentsMapContext.Provider>;

/**
 * The registry of the enclosing provider, or `null` outside one.
 *
 * Returns null rather than throwing. An earlier revision threw, on the reasoning that a
 * silent fallback is the failure this ticket exists to remove — but that was wrong about
 * who renders `Tabs`. `Tabs` is exported through `@ui/cms/componentExports`, which is what
 * **admin** renders from, so it is legitimately mounted outside any `GeneratePage`: the
 * page-builder canvas, the page-preview dialog, and 43 of this package's own tests. Throwing
 * broke all of them, and would have thrown in the admin builder for any page containing a
 * Tabs component. The admin page builder now supplies a provider of its own (the web
 * registry), so its Tabs render their tabs' layouts; the tests render without one.
 *
 * The safety that throw was buying is provided instead by the SSR tests, which assert that
 * a lazy component nested in a Tab renders real markup — i.e. that the provider path is
 * actually the one taken on the server.
 */
export const useCmsComponentsMap = (): CmsComponentsMap | null => useContext(CmsComponentsMapContext);
